import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { incomeAllowed, KEPT_AGE_CAP, placeWords, type Audience, type Place, type SentencePiece, type Start } from "../../../shared/goals/audience";
import type { Spot } from "../../../shared/goals/placements";
import { PhoneNav } from "../../../shared/phone/PhoneNav";
import { PnRow } from "../../../shared/phone/Row";
import { ActionSheet, Sheet } from "../../../shared/phone/Sheet";
import { Icon } from "../../components/ui/Icon";
import type { Dimension, Reach, SearchHit } from "../../lib/audienceApi";
import type { ListsView } from "../../lib/listsApi";
import { SetupRail } from "../ads/SetupRail";
import { FlowBar, StepHead, stepLine } from "../goal/Native";
import { META_STEPS } from "../goal/steps";
import { NativeToast } from "../ads/NativeToast";
import { PlaceSheet, spotsPill } from "../cc/PlaceSheet";
import { shortGoal } from "../cc/text";
import { Stepper } from "../ready/Sending";
import { ageWords, Count, KINDS, lensFacts, START_KINDS, START_WORDS, useFinder } from "./AudienceStep";
import { listsSummary, type CustomAudience, type PixelEvent } from "./ListsBlock";
import { ListsNative } from "./ListsNative";

/** What the Audience step hands its native screen: its data and its actions, the same as on a computer. */
export interface AudCtx {
  goalId: string;
  sentence: string;
  aud: Audience;
  editable: boolean;
  /** The goal is still being set up: the screen is step 5 of the new goal. */
  setup: boolean;
  pill: [string, string];
  checkMessage: string | null;
  pieces: SentencePiece[];
  reach: (Reach | null)[];
  dailyCents: number | null;
  lists: ListsView | null;
  pixelEvents: PixelEvent[] | null;
  custom: CustomAudience[] | null;
  unread: ("events" | "custom")[];
  save: (next: Audience, summary: string) => void;
  addPlace: (h: SearchHit, out: boolean) => void;
  removePlace: (p: Place, out: boolean) => void;
  setRadius: (p: Place, km: number) => void;
  addStart: (h: SearchHit) => void;
  removeStart: (s: Start) => void;
  placesSummary: string;
  whoSummary: string;
  spots: readonly Spot[] | null;
  saveSpots: (next: Spot[] | null) => Promise<boolean>;
  onAddList: () => void;
  toast: ReactNode;
}

const KM = [17, 25, 40, 60, 80];
const PILL: Record<string, string> = { live: "acc", good: "good", tight: "warn", plain: "" };
const REACH = {
  start: ["Let Meta find more people like these", "Places, a youngest age up to 25, and people left out stay fixed."],
  fence: ["Only the people I chose", "Ages and lists stay fixed too. Often costs more per result."],
} as const;

type SheetId = "where" | "who" | "lists" | "reach" | "spots";

/**
 * Who sees the ads on a phone and an iPad: the sentence first, then Where, Who, Client lists, How far and
 * where the ads show as rows that open sheets, and Meta's estimate. Read only once the ads are on Meta.
 */
export function AudienceNative(c: AudCtx) {
  const navigate = useNavigate();
  const [sheet, setSheet] = useState<SheetId | null>(null);
  const { aud, editable, goalId } = c;
  const open = (id: SheetId) => (editable ? () => setSheet(id) : undefined);
  const partSheet: Record<string, SheetId> = { places: "where", ages: "who", starts: "who", lists: "lists", reach: "reach" };
  const f = lensFacts(c.reach, aud, c.dailyCents);
  const tone = PILL[c.pill[0]] ?? "";

  return (
    <div className={c.setup ? "pn-screen ad-scr su-wiz" : "pn-screen ad-scr"}>
      {c.setup ? (
        <FlowBar sub={stepLine(5, META_STEPS.length)} back={{ to: `/goals/${goalId}/plan`, label: "Plan" }} />
      ) : (
        <PhoneNav title="Audience" large back={{ to: `/goals/${goalId}`, label: "Goal" }} sub="Who sees the ads" />
      )}
      {c.setup && <SetupRail goalId={goalId} step={5} />}
      <div className={c.setup ? "pn-stack ad-step" : "pn-stack"}>
        {c.setup && <StepHead title="Audience" sub="Who sees the ads." />}
        <div className="pn-card ad-sent">
          <div className="ad-senth">
            <span className={`pn-pill ${tone}`}>
              <i />
              {c.pill[1]}
            </span>
          </div>
          {c.checkMessage && <p className="ad-sentm">{c.checkMessage}</p>}
          <p className="ad-sentp">
            {c.pieces.map((x, i) =>
              "part" in x ? (
                editable ? (
                  // biome-ignore lint/suspicious/noArrayIndexKey: the sentence's parts keep their order.
                  <span
                    key={i}
                    role="button"
                    tabIndex={0}
                    className={x.look === "start" ? "ad-w soft" : "ad-w"}
                    onClick={() => setSheet(partSheet[x.part])}
                    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setSheet(partSheet[x.part])}
                  >
                    {x.text}
                  </span>
                ) : (
                  // biome-ignore lint/suspicious/noArrayIndexKey: the sentence's parts keep their order.
                  <span key={i} className={x.look === "start" ? "ad-w soft" : "ad-w"}>
                    {x.text}
                  </span>
                )
              ) : (
                // biome-ignore lint/suspicious/noArrayIndexKey: the sentence's parts keep their order.
                <span key={i}>{x.text}</span>
              ),
            )}
          </p>
        </div>

        <div className="pn-grp">
          <PnRow onClick={open("where")} icon={<Icon name="target" />} iconTone={editable ? undefined : "plain"} title="Where" sub={c.placesSummary} wrap />
          <PnRow onClick={open("who")} icon={<Icon name="users" />} iconTone={editable ? undefined : "plain"} title="Who" sub={c.whoSummary} wrap />
          <PnRow onClick={open("lists")} icon={<Icon name="heart" />} iconTone={editable ? undefined : "plain"} title="Client lists" sub={listsSummary(aud, c.lists)} wrap />
          <PnRow onClick={open("reach")} icon={<Icon name="globe" />} iconTone={editable ? undefined : "plain"} title="How far Meta may look" sub={REACH[aud.reach][0]} wrap />
          <PnRow onClick={open("spots")} icon={<Icon name="eye" />} iconTone={editable ? undefined : "plain"} title="Where the ads show" sub={spotsPill(c.spots)} wrap />
        </div>

        <div className="pn-card ad-reach">
          <div className="ad-rh">
            <b>Who Meta can reach</b>
            <span>Meta's estimate</span>
          </div>
          <span className="ad-rbig">
            {f.full?.ready && f.full.lower !== null && f.full.upper !== null ? (
              <>
                <Count n={f.full.lower} /> to <Count n={f.full.upper} />
              </>
            ) : f.counting ? (
              "Meta is still counting"
            ) : (
              "Not counted yet"
            )}
          </span>
          {f.full?.ready && <span className="ad-rsub">{f.sub}</span>}
          {f.start && f.kept?.ready && (
            <p className="ad-rsub">
              Meta may also show the ads to others aged {Math.min(aud.ages.min, KEPT_AGE_CAP)} and over in {f.places} who look likely to respond.
            </p>
          )}
          {f.pos !== null && f.day && (
            <>
              <div className="ad-fitbar">
                <i style={{ left: `calc(${f.pos}% - 2px)` }} />
              </div>
              <div className="ad-fitk">
                <span>Too few</span>
                <span>Right for your budget</span>
                <span>Very wide</span>
              </div>
              <p className="ad-rsub ad-c">{f.band === "few" ? `Few people for ${f.day}. Results may cost more.` : f.band === "wide" ? `Very wide for ${f.day}.` : `Right size for ${f.day}.`}</p>
            </>
          )}
        </div>

        {!c.setup && !editable && (
          <div className="pn-grp">
            <PnRow to={`/goals/${goalId}`} icon={<Icon name="pen" />} iconTone="plain" title="Change it through the goal's chat" sub="The ads are on Meta, so the agent makes the change there first." wrap />
          </div>
        )}
      </div>

      {c.setup && (
        <div className="pn-foot">
          <button type="button" className="pn-btn pri wide" onClick={() => navigate(`/goals/${goalId}/ads`)}>
            Next: Ads
            <Icon name="right" />
          </button>
        </div>
      )}

      <WhereSheet c={c} open={sheet === "where"} onClose={() => setSheet(null)} />
      <WhoSheet c={c} open={sheet === "who"} onClose={() => setSheet(null)} />
      <Sheet
        open={sheet === "lists"}
        onClose={() => setSheet(null)}
        tall
        title="Client lists"
        right={
          <button type="button" className="pn-tbtn strong" onClick={() => setSheet(null)}>
            Done
          </button>
        }
      >
        <ListsNative aud={aud} view={c.lists} events={c.pixelEvents} custom={c.custom} unread={c.unread} editable={editable} save={c.save} onAdd={c.onAddList} />
      </Sheet>
      <Sheet open={sheet === "reach"} onClose={() => setSheet(null)} title="How far Meta may look">
        <div className="pn-grp" role="radiogroup" aria-label="How far Meta may look">
          {(["start", "fence"] as const).map((id) => (
            <button
              key={id}
              type="button"
              className="pn-row wrap"
              role="radio"
              aria-checked={aud.reach === id}
              onClick={() => {
                setSheet(null);
                if (aud.reach !== id) c.save({ ...aud, reach: id }, `Who sees the ads: ${REACH[id][0].toLowerCase()}`);
              }}
            >
              <span className="m">
                <span className="t">
                  {REACH[id][0]}
                  {id === "start" && <span className="pn-pill acc ad-sugg">Meta suggests</span>}
                </span>
                <span className="s">{REACH[id][1]}</span>
              </span>
              <span className={aud.reach === id ? "ad-tick" : "ad-tick off"}>
                <Icon name="check" />
              </span>
            </button>
          ))}
        </div>
        {aud.why.reach && (
          <p className="pn-note">
            <b>Why:</b> {aud.why.reach}
          </p>
        )}
      </Sheet>
      <PlaceSheet open={sheet === "spots"} onClose={() => setSheet(null)} title={shortGoal(c.sentence)} ads={0} onMeta={false} spots={c.spots} onSave={c.saveSpots} />
      <NativeToast>{c.toast}</NativeToast>
    </div>
  );
}

/** A search over Meta's own list, drawn as the phone's search field with its results as rows. */
function Search({ goalId, dimension, placeholder, onPick }: { goalId: string; dimension: Dimension | "relationship"; placeholder: string; onPick: (h: SearchHit) => void }) {
  const { q, setQ, hits, error, busy } = useFinder(goalId, dimension);
  return (
    <>
      <label className="pn-search ad-srch">
        <Icon name="search" />
        <input type="search" value={q} placeholder={placeholder} aria-label={placeholder} onChange={(e) => setQ(e.target.value)} />
      </label>
      {(busy || error || hits) && (
        <div className="pn-grp ad-hits" aria-busy={busy}>
          {busy && (
            <div className="pn-row static">
              <span className="ad-spin" aria-hidden="true" />
              <span className="m">
                <span className="t">Searching Meta's list</span>
              </span>
            </div>
          )}
          {error && !busy && <PnRow title={error} wrap />}
          {!busy &&
            hits?.map((h, i) => {
              const name = "place" in h ? h.place.name : h.start.name;
              const kind = "place" in h ? KINDS[h.place.kind] : START_WORDS[h.start.kind];
              const where = h.path.length > 1 ? h.path.slice(0, -1).join(", ") : "";
              return (
                <PnRow
                  // biome-ignore lint/suspicious/noArrayIndexKey: Meta's answers keep their order.
                  key={i}
                  onClick={() => {
                    onPick(h);
                    setQ("");
                  }}
                  icon={<Icon name="plus" />}
                  title={name}
                  sub={[kind, where].filter(Boolean).join(" · ")}
                  chevron={false}
                />
              );
            })}
          {!busy && hits && hits.length === 0 && !error && <PnRow title="Meta has nothing for that." />}
        </div>
      )}
    </>
  );
}

function WhereSheet({ c, open, onClose }: { c: AudCtx; open: boolean; onClose: () => void }) {
  const { aud, goalId } = c;
  const [mode, setMode] = useState<"in" | "out">("in");
  const [km, setKm] = useState<Place | null>(null);
  const [gone, setGone] = useState<{ p: Place; out: boolean } | null>(null);
  useEffect(() => {
    if (open) setMode("in");
  }, [open]);
  const placeRow = (p: Place, out: boolean) => (
    <div className="pn-row" key={`${p.kind}:${p.key ?? p.name}`}>
      <span className={out ? "pn-ico danger" : "pn-ico"}>
        <Icon name={out ? "ban" : "target"} />
      </span>
      <span className="m">
        <span className="t">{placeWords({ ...p, radiusKm: undefined })}</span>
        <span className="s">{KINDS[p.kind]}</span>
      </span>
      {!out && p.kind === "city" && (
        <button type="button" className="ad-kmb" onClick={() => setKm(p)} aria-label={`Distance around ${p.name}`}>
          +{p.radiusKm ?? 25} km
          <Icon name="chev" />
        </button>
      )}
      <button type="button" className="ad-x" aria-label={out ? `Stop leaving out ${p.name}` : `Remove ${p.name}`} onClick={() => setGone({ p, out })}>
        <Icon name="x" />
      </button>
    </div>
  );
  return (
    <>
      <Sheet
        open={open}
        onClose={onClose}
        tall
        title="Where"
        right={
          <button type="button" className="pn-tbtn strong" onClick={onClose}>
            Done
          </button>
        }
      >
        <Search key={mode} goalId={goalId} dimension="geo" placeholder={mode === "in" ? "Add a city, region or ZIP code" : "A place to leave out"} onPick={(h) => c.addPlace(h, mode === "out")} />
        <div className="pn-cap ad-mt">Show the ads to people in</div>
        <div className="pn-grp">
          {aud.places.include.map((p) => placeRow(p, false))}
          {mode === "out" && <PnRow className="accent" onClick={() => setMode("in")} icon={<Icon name="plus" />} title="Add a place" chevron={false} />}
        </div>
        <div className="pn-cap ad-mt">Leave out</div>
        <div className="pn-grp">
          {aud.places.exclude.map((p) => placeRow(p, true))}
          {mode === "in" && <PnRow className="accent" onClick={() => setMode("out")} icon={<Icon name="plus" />} title="Leave out a place" chevron={false} />}
        </div>
        <p className="pn-note">Places are always kept: Meta never shows the ads outside them.</p>
        {aud.why.places && (
          <p className="pn-note">
            <b>Why these places:</b> {aud.why.places}
          </p>
        )}
      </Sheet>
      <ActionSheet
        open={!!km}
        onClose={() => setKm(null)}
        title={km ? `How far around ${km.name}` : ""}
        actions={KM.map((d) => ({
          label: (
            <span className="ad-kml">
              {d} km{(km?.radiusKm ?? 25) === d && <Icon name="check" />}
            </span>
          ),
          onSelect: () => km && (km.radiusKm ?? 25) !== d && c.setRadius(km, d),
        }))}
      />
      <ActionSheet
        open={!!gone}
        onClose={() => setGone(null)}
        title={gone ? (gone.out ? `Stop leaving out ${gone.p.name}?` : `Remove ${gone.p.name}?`) : ""}
        actions={gone ? [{ label: gone.out ? "Stop leaving it out" : "Remove it", danger: true, onSelect: () => c.removePlace(gone.p, gone.out) }] : []}
      />
    </>
  );
}

function WhoSheet({ c, open, onClose }: { c: AudCtx; open: boolean; onClose: () => void }) {
  const { aud, goalId } = c;
  const [ages, setAges] = useState<[number, number]>([aud.ages.min, aud.ages.max]);
  const [adding, setAdding] = useState(false);
  const [kind, setKind] = useState<Dimension | "relationship">("interest");
  useEffect(() => {
    if (!open) return;
    setAges([aud.ages.min, aud.ages.max]);
    setAdding(false);
  }, [open, aud.ages.min, aud.ages.max]);
  const done = () => {
    const [min, max] = ages;
    if (min !== aud.ages.min || max !== aud.ages.max) c.save({ ...aud, ages: { min, max } }, `Who sees the ads: aged ${min} to ${max >= 65 ? "65+" : max}`);
    onClose();
  };
  const floor = Math.min(ages[0], KEPT_AGE_CAP);
  return (
    <Sheet
      open={open}
      onClose={done}
      tall
      title="Who"
      right={
        <button type="button" className="pn-tbtn strong" onClick={done}>
          Done
        </button>
      }
    >
      <div className="pn-cap">Ages</div>
      <div className="pn-grp">
        <Stepper label="Youngest" value={ages[0]} min={18} max={ages[1]} onChange={(n) => setAges([n, ages[1]])} />
        <Stepper label="Oldest" value={ages[1]} shown={ages[1] >= 65 ? "65+" : String(ages[1])} min={ages[0]} max={65} onChange={(n) => setAges([ages[0], n])} />
      </div>
      <p className="pn-note">{aud.reach === "start" ? `Always kept: ${floor} and over. Meta starts with ${ageWords(ages[0], ages[1])} and may go wider.` : `Only ${ageWords(ages[0], ages[1])}.`}</p>
      <div className="pn-cap ad-mt">Who Meta starts with</div>
      <div className="pn-grp">
        {aud.starts.map((s) => (
          <div className="pn-row" key={`${s.kind}:${s.id}`}>
            <span className="m">
              <span className="t">{s.name}</span>
              <span className="s">{START_WORDS[s.kind]}</span>
            </span>
            <button type="button" className="ad-x" aria-label={`Remove ${s.name}`} onClick={() => c.removeStart(s)}>
              <Icon name="x" />
            </button>
          </div>
        ))}
        {!adding && <PnRow className="accent" onClick={() => setAdding(true)} icon={<Icon name="plus" />} title="Add a starting point" chevron={false} />}
      </div>
      {adding && (
        <>
          <div className="pn-chips ad-kinds" role="group" aria-label="Kind of starting point">
            {START_KINDS.filter(([k]) => k !== "income" || incomeAllowed(aud)).map(([k, l]) => (
              <button key={k} type="button" className={kind === k ? "pn-chip on" : "pn-chip"} aria-pressed={kind === k} onClick={() => setKind(k)}>
                {l}
              </button>
            ))}
          </div>
          <Search
            key={kind}
            goalId={goalId}
            dimension={kind}
            placeholder="An interest, a job title or a habit"
            onPick={(h) => {
              c.addStart(h);
              setAdding(false);
            }}
          />
        </>
      )}
      <p className="pn-note">Meta starts with these people and may go wider when it finds more like them.</p>
      {(aud.why.ages || aud.why.starts) && (
        <p className="pn-note">
          <b>Why these:</b> {[aud.why.ages, aud.why.starts].filter(Boolean).join(" ")}
        </p>
      )}
    </Sheet>
  );
}
